import { LoaderCircle, RefreshCw, User } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { TApiServiceError } from "@/api/apiService";
import { type TSectionInitialData } from "@/api/dashboard/dashboard.types";
import {
  dealNetworkService,
  type TDealNetworkConnectRequest,
  type TDealNetworkFounderRecommendationCard,
} from "@/api/dealNetwork";
import { FilterPillsBar } from "@/components/dashboard/FilterPillsBar";
import { SectionCard } from "@/components/dashboard/SectionCard";
import { SectionCardChatInput } from "@/components/dashboard/SectionCardChatInput";
import { SectionCardSkeleton } from "@/components/dashboard/SectionCardSkeleton";
import { PageHeader } from "@/components/layout";
import { AiAssistedDisclaimer } from "@/components/shared";
import { useToast } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import type { TApiResponse } from "@/types/api";
import type { TAsyncTuple } from "@/utils";
import { getIntroductionStatusMessage } from "@/utils/connectRequestStatus";
import { extractErrorMessage } from "@/utils/errorUtils";
import { getScoreBadgeClass } from "@/utils/scoreBadge";

type TDealPipelineFeatureProps = {
  dashboardData: TSectionInitialData | null;
  isLoading: boolean;
  error: string | null;
  companyId?: string | null;
  sectionId?: string | null;
  /** Section title rendered at the top of the scrollable area. */
  title: string;
  /** Section description rendered under the title. */
  description?: string | null;
};

function getFounderDisplayName(card: TDealNetworkFounderRecommendationCard): string {
  const f = card.founder ?? card.founder_user;
  if (f) return `${f.first_name} ${f.last_name}`.trim();
  const company = card.founder_company ?? card.fundraiser_company;
  return company?.name ?? "Founder";
}

type TDealCardProps = {
  recommendation: TDealNetworkFounderRecommendationCard;
  companyId: string | null;
  sectionId: string | null;
  onRequestIntroduction: (recommendation: TDealNetworkFounderRecommendationCard) => void;
  onShortlist: (recommendation: TDealNetworkFounderRecommendationCard) => void;
  onDismiss: (recommendation: TDealNetworkFounderRecommendationCard) => void;
  isRequestLoading?: boolean;
  isShortlistLoading?: boolean;
  isDismissLoading?: boolean;
};

function DealCard({
  recommendation,
  companyId,
  sectionId,
  onRequestIntroduction,
  onShortlist,
  onDismiss,
  isRequestLoading = false,
  isShortlistLoading = false,
  isDismissLoading = false,
}: TDealCardProps) {
  const name = getFounderDisplayName(recommendation);
  const company = (recommendation.founder_company ?? recommendation.fundraiser_company)?.name ?? "";
  const score = recommendation.score;
  const scoreDisplay = score !== null && score !== undefined ? `${score}/100` : "N/A";
  const introductionStatusMessage = getIntroductionStatusMessage(recommendation.connect_request?.status?.name);
  const isPending = recommendation.connect_request?.status?.name?.toLowerCase() === "pending";
  const showRequestIntroductionButton = !recommendation.connect_request;
  const showRetryButton = Boolean(recommendation.connect_request && isPending);
  const founderCompanyId = (recommendation.founder_company ?? recommendation.fundraiser_company)?.id ?? "";

  return (
    <SectionCard
      headerLeft={
        <div className="flex min-w-0 flex-1 items-start gap-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600">
            <User className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground sm:text-base">{name}</p>
            <p className="text-xs text-muted-foreground sm:text-sm">{company}</p>
          </div>
        </div>
      }
      headerRight={
        <div
          className={cn(
            "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-2 p-2 text-[10px] font-semibold leading-tight sm:h-12 sm:w-12 sm:p-2 sm:text-xs",
            score != null ? getScoreBadgeClass(score) : "border-border text-muted-foreground"
          )}
        >
          <span className="whitespace-nowrap">{scoreDisplay}</span>
        </div>
      }
      actions={
        <>
          {showRequestIntroductionButton ? (
            <Button
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => onRequestIntroduction(recommendation)}
              disabled={isRequestLoading}
            >
              {isRequestLoading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
              Request Introduction
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onShortlist(recommendation)}
            disabled={isShortlistLoading}
          >
            {isShortlistLoading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
            {recommendation.shortlisted ? "Remove from shortlist" : "Shortlist"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDismiss(recommendation)}
            disabled={isDismissLoading}
          >
            {isDismissLoading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
            {recommendation.dismissed ? "Undo dismiss" : "Dismiss"}
          </Button>
        </>
      }
      footer={
        companyId && sectionId ? (
          <SectionCardChatInput
            mode="investorChat"
            investorCompanyId={companyId}
            sectionId={sectionId}
            founderCompanyId={founderCompanyId}
            dealName={name}
            placeholder="Ask a question..."
          />
        ) : null
      }
    >
      <AiAssistedDisclaimer
        variant="compact"
        context="investment"
        className="mb-2"
      />
      <p className="text-sm leading-relaxed text-muted-foreground">{recommendation.assessment}</p>

      {recommendation.section_categories?.length ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {recommendation.section_categories.map((cat) => (
            <span
              key={cat.id}
              className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
            >
              {cat.name}
            </span>
          ))}
        </div>
      ) : null}

      {introductionStatusMessage ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/20 bg-primary/10 p-4">
          <span className="text-sm font-semibold text-primary">{introductionStatusMessage}</span>
          {showRetryButton ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRequestIntroduction(recommendation)}
              disabled={isRequestLoading}
              className="border-primary/30 text-primary hover:bg-primary/10"
            >
              {isRequestLoading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
              Retry
            </Button>
          ) : null}
        </div>
      ) : null}
    </SectionCard>
  );
}

// Special category IDs for filtering
const SHORTLIST_CATEGORY_ID = "__shortlist__";
const DISMISSED_CATEGORY_ID = "__dismissed__";

/**
 * My Deal Pipeline dashboard body. Renders two siblings inside PageLayout's
 * flex column: a fixed category filter bar, then the scrollable area holding
 * the section title/description card and the recommendations list.
 */
export function DealPipelineFeature({
  dashboardData,
  isLoading,
  error,
  companyId,
  sectionId,
  title,
  description,
}: TDealPipelineFeatureProps) {
  const { toast } = useToast();
  const apiCategories = (dashboardData?.categories ?? []).map((cat) => ({
    id: cat.categoryId,
    name: cat.categoryName,
  }));

  // Add special categories for shortlist and dismissed at the beginning
  const categories = [
    { id: SHORTLIST_CATEGORY_ID, name: "Shortlisted" },
    { id: DISMISSED_CATEGORY_ID, name: "Dismissed" },
    ...apiCategories,
  ];

  const [recommendations, setRecommendations] = useState<TDealNetworkFounderRecommendationCard[]>([]);
  const [isRecommendationsLoading, setIsRecommendationsLoading] = useState(true);
  const [recommendationsError, setRecommendationsError] = useState<string | null>(null);
  const [requestIntroLoading, setRequestIntroLoading] = useState<Record<string, boolean>>({});
  const [shortlistLoading, setShortlistLoading] = useState<Record<string, boolean>>({});
  const [dismissLoading, setDismissLoading] = useState<Record<string, boolean>>({});
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Refresh status tracking
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState<"idle" | "starting" | "in_progress" | "completed" | "error">(
    "idle"
  );
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshSuccessBrief, setRefreshSuccessBrief] = useState(false);

  // Refs for polling cleanup
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentRecommendationIdsRef = useRef<Set<string>>(new Set());

  // Helper function to fetch recommendations
  const fetchRecommendations = useCallback(async () => {
    if (!companyId) {
      setRecommendations([]);
      setRecommendationsError(null);
      setIsRecommendationsLoading(false);
      return;
    }

    setIsRecommendationsLoading(true);
    setRecommendationsError(null);

    // Determine query parameters based on selected category
    let queryParams: { shortlisted?: boolean; dismissed?: boolean } | undefined;
    if (selectedCategoryId === SHORTLIST_CATEGORY_ID) {
      queryParams = { shortlisted: true };
    } else if (selectedCategoryId === DISMISSED_CATEGORY_ID) {
      queryParams = { dismissed: true };
    }

    // Fetch recommendations with optional filters
    const { data, error: fetchError } = await dealNetworkService.getFounderRecommendations(companyId, queryParams);

    if (fetchError) {
      const message = await extractErrorMessage(fetchError);
      setRecommendationsError(message ?? "Unable to load founder recommendations.");
      setRecommendations([]);
    } else {
      const fetchedRecommendations = data?.data ?? [];
      setRecommendations(fetchedRecommendations);

      // Update current recommendation IDs for polling comparison
      currentRecommendationIdsRef.current = new Set(
        fetchedRecommendations.map((rec) => rec.id ?? rec.recommendation_id ?? "").filter(Boolean)
      );
    }
    setIsRecommendationsLoading(false);
  }, [companyId, selectedCategoryId]);

  // Effect to fetch recommendations when category selection changes
  useEffect(() => {
    void fetchRecommendations();
  }, [fetchRecommendations]);

  // Cleanup polling on unmount
  const clearPollingTimers = useCallback(() => {
    const timeoutId = pollTimeoutRef.current;
    const intervalId = pollIntervalRef.current;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (intervalId) {
      clearTimeout(intervalId);
    }
  }, []);

  useEffect(() => clearPollingTimers, [clearPollingTimers]);

  // Brief success state for Refresh button (2s) when refresh completes
  useEffect(() => {
    if (refreshStatus === "completed") {
      setRefreshSuccessBrief(true);
      const t = setTimeout(() => setRefreshSuccessBrief(false), 2000);
      return () => clearTimeout(t);
    }
    setRefreshSuccessBrief(false);
  }, [refreshStatus]);

  // Function to trigger refresh with polling
  const triggerRefresh = useCallback(async () => {
    if (!companyId || isRefreshing) {
      if (isRefreshing) {
        toast({
          title: "Refresh in progress",
          description: "Please wait for the current refresh to complete.",
          variant: "default",
        });
      }
      return;
    }

    setIsRefreshing(true);
    setRefreshStatus("starting");
    setRefreshError(null);

    try {
      // Trigger refresh endpoint
      const { data, error: refreshError } = await dealNetworkService.refreshFounderRecommendations(companyId);

      if (refreshError) {
        const errorMessage = await extractErrorMessage(refreshError);

        // Handle specific error cases
        if (refreshError.response?.status === 400) {
          setRefreshError("Only investor companies can refresh recommendations.");
          toast({
            title: "Unable to refresh",
            description: "Only investor companies can refresh recommendations.",
            variant: "destructive",
          });
        } else if (refreshError.response?.status === 403) {
          setRefreshError("You do not have permission to refresh recommendations for this company.");
          toast({
            title: "Permission denied",
            description: "You do not have permission to refresh recommendations for this company.",
            variant: "destructive",
          });
        } else {
          setRefreshError(errorMessage ?? "Failed to start refresh");
          toast({
            title: "Refresh failed",
            description: errorMessage ?? "Failed to start refresh. Please try again.",
            variant: "destructive",
          });
        }

        setRefreshStatus("error");
        setIsRefreshing(false);
        return;
      }

      // Show success message
      const message =
        (data?.data as { message?: string })?.message ??
        "Founder recommendations refresh started. New recommendations will appear in your list shortly.";

      toast({
        title: "Refresh started",
        description: message,
        variant: "default",
      });

      setRefreshStatus("in_progress");

      // Store current recommendation IDs before polling
      const initialIds = new Set(currentRecommendationIdsRef.current);

      // Poll once after 20 seconds to pick up refreshed recommendations
      pollTimeoutRef.current = setTimeout(() => {
        const pollForRecommendations = async () => {
          try {
            // Determine query parameters based on selected category
            let queryParams: { shortlisted?: boolean; dismissed?: boolean } | undefined;
            if (selectedCategoryId === SHORTLIST_CATEGORY_ID) {
              queryParams = { shortlisted: true };
            } else if (selectedCategoryId === DISMISSED_CATEGORY_ID) {
              queryParams = { dismissed: true };
            }

            const { data: pollData, error: pollError } = await dealNetworkService.getFounderRecommendations(
              companyId,
              queryParams
            );

            if (pollError) {
              console.error("[deal-pipeline] Failed to poll refreshed recommendations", {
                error: pollError,
                companyId,
              });
              setRefreshStatus("error");
              setRefreshError("Failed to fetch updated recommendations");
              setIsRefreshing(false);
              return;
            }

            const updatedRecommendations = pollData?.data ?? [];

            // Check for new recommendations (IDs we didn't have before refresh)
            const newRecommendations = updatedRecommendations.filter((rec) => {
              const recId = rec.id ?? rec.recommendation_id ?? "";
              return recId && !initialIds.has(recId);
            });

            // Update recommendations list
            setRecommendations(updatedRecommendations);
            currentRecommendationIdsRef.current = new Set(
              updatedRecommendations.map((rec) => rec.id ?? rec.recommendation_id ?? "").filter(Boolean)
            );

            setRefreshStatus("completed");
            setIsRefreshing(false);
            if (newRecommendations.length > 0) {
              toast({
                title: "Refresh completed",
                description: `Found ${newRecommendations.length} new recommendation${newRecommendations.length > 1 ? "s" : ""}.`,
                variant: "default",
              });
            } else {
              toast({
                title: "Refresh completed",
                description: "Recommendations have been updated.",
                variant: "default",
              });
            }
          } catch (err) {
            console.error("[deal-pipeline] Unexpected error while polling recommendations", {
              error: err,
              companyId,
            });
            setRefreshStatus("error");
            setRefreshError("Failed to fetch updated recommendations");
            setIsRefreshing(false);
          }
        };

        // Single poll after delay (no repeated polling)
        void pollForRecommendations();
      }, 20000); // Wait 20 seconds for background refresh, then fetch once
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to start refresh";
      setRefreshStatus("error");
      setRefreshError(errorMessage);
      setIsRefreshing(false);
      toast({
        title: "Refresh failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  }, [companyId, isRefreshing, selectedCategoryId, toast]);

  const handleRequestIntroduction = useCallback(
    (recommendation: TDealNetworkFounderRecommendationCard) => {
      const rid = recommendation.id ?? recommendation.recommendation_id;
      if (!rid) return;
      setRequestIntroLoading((prev) => ({ ...prev, [rid]: true }));

      void (async () => {
        try {
          const response: TAsyncTuple<
            TApiServiceError,
            TApiResponse<TDealNetworkConnectRequest>
          > = await dealNetworkService.createFounderRecommendationConnectRequest(rid);
          const { data, error } = response;

          if (error) {
            const message = await extractErrorMessage(error);
            toast({
              title: "Unable to request introduction",
              description: message,
              variant: "destructive",
            });
            return;
          }

          if (data?.data) {
            setRecommendations((prev) =>
              prev.map((rec) =>
                rec.id === rid || rec.recommendation_id === rid ? { ...rec, connect_request: data.data } : rec
              )
            );
            toast({
              title: "Request sent",
              description: "Your introduction request has been sent to Fundy.",
            });
          }
        } finally {
          setRequestIntroLoading((prev) => {
            const next = { ...prev };
            delete next[rid];
            return next;
          });
        }
      })();
    },
    [toast]
  );

  const handleShortlist = useCallback(
    (recommendation: TDealNetworkFounderRecommendationCard) => {
      const rid = recommendation.id ?? recommendation.recommendation_id;
      if (!rid) return;
      setShortlistLoading((prev) => ({ ...prev, [rid]: true }));

      void (async () => {
        try {
          const { data, error } = await dealNetworkService.updateFounderRecommendation(rid, {
            shortlisted: !recommendation.shortlisted,
          });

          if (error) {
            const message = await extractErrorMessage(error);
            toast({
              title: "Unable to update recommendation",
              description: message,
              variant: "destructive",
            });
            return;
          }

          if (data?.data) {
            // Update the recommendation in the local state
            setRecommendations((prev) =>
              prev.map((rec) => (rec.id === rid || rec.recommendation_id === rid ? data.data : rec))
            );
            toast({
              title: recommendation.shortlisted ? "Removed from shortlist" : "Added to shortlist",
              description: recommendation.shortlisted
                ? "The recommendation has been removed from your shortlist."
                : "The recommendation has been added to your shortlist.",
            });
          }
        } finally {
          setShortlistLoading((prev) => {
            const next = { ...prev };
            delete next[rid];
            return next;
          });
        }
      })();
    },
    [toast]
  );

  const handleDismiss = useCallback(
    (recommendation: TDealNetworkFounderRecommendationCard) => {
      const rid = recommendation.id ?? recommendation.recommendation_id;
      if (!rid) return;
      setDismissLoading((prev) => ({ ...prev, [rid]: true }));

      void (async () => {
        try {
          const { data, error } = await dealNetworkService.updateFounderRecommendation(rid, {
            dismissed: !recommendation.dismissed,
          });

          if (error) {
            const message = await extractErrorMessage(error);
            toast({
              title: "Unable to update recommendation",
              description: message,
              variant: "destructive",
            });
            return;
          }

          if (data?.data) {
            // Update the recommendation in the local state
            setRecommendations((prev) =>
              prev.map((rec) => (rec.id === rid || rec.recommendation_id === rid ? data.data : rec))
            );
            toast({
              title: recommendation.dismissed ? "Undismissed" : "Dismissed",
              description: recommendation.dismissed
                ? "The recommendation has been undismissed."
                : "The recommendation has been dismissed.",
            });
          }
        } finally {
          setDismissLoading((prev) => {
            const next = { ...prev };
            delete next[rid];
            return next;
          });
        }
      })();
    },
    [toast]
  );

  const handleDownloadReport = useCallback(() => {
    if (!companyId) {
      setDownloadError("Select an investor company before downloading the PDF.");
      return;
    }

    setIsDownloadingReport(true);
    setDownloadError(null);

    void (async () => {
      const { data, error } = await dealNetworkService.downloadFounderRecommendationsReport(companyId);
      setIsDownloadingReport(false);

      if (error || !data) {
        const statusCode = (error as TApiServiceError | undefined)?.response?.status;
        let message = "Unable to download founder recommendations. Please try again.";
        if (statusCode === 404) {
          message = "No founder recommendations available to download yet.";
        } else if (statusCode === 400) {
          message = "Only investor companies can download this PDF.";
        }
        setDownloadError(message);
        toast({
          title: "Download failed",
          description: message,
          variant: "destructive",
        });
        return;
      }

      const blobUrl = URL.createObjectURL(data.blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = data.filename ?? "founder_recommendations.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      toast({
        title: "Download started",
        description: "Founder recommendations PDF is downloading.",
      });
    })();
  }, [companyId, toast]);

  const filteredRecommendations = (() => {
    // Special categories are already filtered by the API, so return as-is
    if (selectedCategoryId === SHORTLIST_CATEGORY_ID || selectedCategoryId === DISMISSED_CATEGORY_ID) {
      return recommendations;
    }
    // Regular category filtering
    if (selectedCategoryId) {
      return recommendations.filter((r) => r.section_categories.some((c) => c.id === selectedCategoryId));
    }
    return recommendations;
  })();

  return (
    <>
      {/* Fixed category filter bar — stays put while the content below scrolls.
          Uses categories from /api/v1/dashboard/section/{section_id}/initialdata */}
      <div className="flex-shrink-0">
        <FilterPillsBar>
          <Button
            size="sm"
            variant={selectedCategoryId === null ? "default" : "outline"}
            onClick={(e) => {
              setSelectedCategoryId(null);
              e.currentTarget.blur();
            }}
            disabled={isLoading}
            className="flex-shrink-0"
          >
            All
          </Button>
          {isLoading && !dashboardData?.categories?.length ? (
            <div className="flex animate-pulse gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-8 w-24 rounded-md bg-muted"
                />
              ))}
            </div>
          ) : (
            categories.map((cat) => (
              <Button
                key={cat.id}
                size="sm"
                variant={selectedCategoryId === cat.id ? "default" : "outline"}
                onClick={(e) => {
                  setSelectedCategoryId(cat.id);
                  e.currentTarget.blur();
                }}
                disabled={isLoading}
                className="flex-shrink-0"
              >
                {cat.name}
              </Button>
            ))
          )}
        </FilterPillsBar>
      </div>

      {/* Scrollable content */}
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex w-full flex-col gap-4">
          <PageHeader
            title={title}
            subtitle={description ?? undefined}
          />

          {/* Header with refresh button */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold text-foreground sm:text-lg">Founder Recommendations</h2>
              {refreshStatus === "in_progress" && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Refresh in progress. New recommendations will appear shortly...
                </p>
              )}
              {refreshStatus === "completed" && (
                <p className="mt-1 text-sm text-green-600">Refresh completed successfully.</p>
              )}
              {refreshStatus === "error" && refreshError && <p className="mt-1 text-sm text-red-600">{refreshError}</p>}
            </div>
            <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
              <Button
                size="sm"
                className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={handleDownloadReport}
                disabled={!companyId || isDownloadingReport}
                loading={isDownloadingReport}
              >
                Download report
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void triggerRefresh()}
                disabled={isRefreshing || !companyId}
                className="gap-2"
                loading={isRefreshing}
                success={refreshSuccessBrief}
                error={refreshStatus === "error"}
              >
                <RefreshCw className="h-4 w-4" />
                <span>Refresh</span>
              </Button>
            </div>
          </div>

          {downloadError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{downloadError}</div>
          )}

          {(error ?? recommendationsError) && (
            <p className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error ?? recommendationsError}
            </p>
          )}

          {isRecommendationsLoading && !recommendations.length ? (
            <div className="space-y-4">
              <SectionCardSkeleton variant="requestIntro" />
              <SectionCardSkeleton variant="requestIntro" />
            </div>
          ) : filteredRecommendations.length > 0 ? (
            <div className="space-y-4">
              {filteredRecommendations.map((rec) => {
                const recId = rec.id ?? rec.recommendation_id ?? "";
                return (
                  <DealCard
                    key={recId}
                    recommendation={rec}
                    companyId={companyId ?? null}
                    sectionId={sectionId ?? null}
                    onRequestIntroduction={handleRequestIntroduction}
                    onShortlist={handleShortlist}
                    onDismiss={handleDismiss}
                    isRequestLoading={requestIntroLoading[recId]}
                    isShortlistLoading={shortlistLoading[recId]}
                    isDismissLoading={dismissLoading[recId]}
                  />
                );
              })}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border bg-muted p-6 text-center text-sm text-muted-foreground">
              {selectedCategoryId === SHORTLIST_CATEGORY_ID
                ? "No shortlisted recommendations yet."
                : selectedCategoryId === DISMISSED_CATEGORY_ID
                  ? "No dismissed recommendations yet."
                  : selectedCategoryId
                    ? "No founder recommendations match the selected category."
                    : "No founder recommendations yet. Check back soon."}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
